import React, { useCallback, useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import Typography from '@mui/material/Typography'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import { useStopwatch, useTimer } from 'react-timer-hook'
import { useReactMediaRecorder } from '../../MediaRecorder'
import redColor from '@mui/material/colors/red'
import useStateCallback from '../../hooks/useStateCallback'
import MenuItem from '@mui/material/MenuItem'
import Menu from '@mui/material/Menu'
import SettingsIcon from '@mui/icons-material/Settings'
import Divider from '@mui/material/Divider'
import useAudioAnalyser from './useAudioAnalyzer'
import { CollectionThemeProps } from '../../../lib/types'

const AudioPreview = ({ stream }: { stream: MediaStream | null }) => {
  const analyser = useAudioAnalyser(stream)
  const visualizerRef = useRef<HTMLCanvasElement>(null)
  const boxRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!analyser) {
      return
    }
    let raf: number

    const data = new Uint8Array(analyser.frequencyBinCount)

    const draw = () => {
      raf = requestAnimationFrame(draw)
      analyser.getByteTimeDomainData(data)
      const canvas = visualizerRef.current

      if (canvas) {
        const { height, width } = canvas
        const context = canvas.getContext('2d')
        let x = 0
        const sliceWidth = (width * 1.0) / data.length

        if (context) {
          context.lineWidth = 2
          context.strokeStyle = '#fff'
          context.clearRect(0, 0, width, height)

          context.beginPath()
          context.moveTo(0, height / 2)
          // @ts-ignore
          for (const item of data) {
            const y = (item / 255.0) * height
            context.lineTo(x, y)
            x += sliceWidth
          }
          context.lineTo(x, height / 2)
          context.stroke()
        }
      }
    }
    draw()

    return () => {
      cancelAnimationFrame(raf)
    }
  }, [visualizerRef, analyser])

  return (
    <Box
      ref={boxRef}
      sx={{
        width: '100%',
        overflow: 'hidden'
      }}
    >
      {analyser && <canvas ref={visualizerRef} width={850} />}
    </Box>
  )
}

const VideoPreview = ({ stream }: { stream: MediaStream | null }) => {
  const videoRef = useCallback(node => {
    if (node && stream) {
      if (window.URL) {
        node.srcObject = stream
      } else {
        node.src = stream
      }
    }
  }, [])
  if (!stream) {
    return null
  }
  return (
    <video
      height="99%"
      width="auto"
      ref={videoRef}
      autoPlay
      muted
      playsInline
      className="video-element"
      style={{ maxWidth: '99%', maxHeight: '99%', borderRadius: '20px' }}
    />
  )
}

const CountDown: React.FC<{ expire: () => void }> = ({ expire }) => {
  const time = new Date()
  time.setSeconds(time.getSeconds() + 3)
  const { seconds } = useTimer({
    expiryTimestamp: time,
    autoStart: true,
    onExpire: expire
  })
  return (
    <Stack spacing={2} direction="column" alignItems="center">
      <Typography
        variant="h4"
        sx={{
          color: redColor.A100
        }}
      >
        Starting
      </Typography>
      <Typography
        variant="h3"
        sx={{
          color: redColor.A100
        }}
      >
        {seconds}
      </Typography>
    </Stack>
  )
}

const useCountDown = (countdown = 0, expire?: () => void) => {
  const time = new Date()
  time.setSeconds(time.getSeconds() + countdown)
  const timer = useTimer({
    expiryTimestamp: time,
    autoStart: false,
    onExpire: expire
  })
  const stopwatch = useStopwatch({ autoStart: false })

  const current = countdown ? timer : stopwatch
  const Component = () => (
    <Typography>{`${current.hours.toString().padStart(2, '0')}:${current.minutes
      .toString()
      .padStart(2, '0')}:${current.seconds.toString().padStart(2, '0')}`}</Typography>
  )
  return {
    reset: countdown ? () => timer.restart(time, false) : stopwatch.reset,
    start: current.start,
    pause: current.pause,
    Component
  }
}

interface iWrapper {
  onDrop: (value: File | null) => void
  isRecordingType?: 'AUDIO' | 'VIDEO' | null
  countdown?: number
  onChangeRecordingType: () => void
  hardStop?: boolean
  collectionTheme?: CollectionThemeProps
}

const Recorder: React.FC<iWrapper> = ({
  countdown,
  hardStop,
  isRecordingType,
  onDrop,
  onChangeRecordingType,
  collectionTheme
}) => {
  const [showCountdown, setShowCountdown] = useStateCallback<boolean>(false)
  const [hideStartRecording, setHideStartRecording] = useStateCallback<boolean>(false)
  const {
    previewStream,
    status,
    startRecording,
    stopRecording,
    mediaBlobUrl,
    clearBlobUrl,
    devices,
    currentDevices,
    switchDevice
  } = useReactMediaRecorder({
    video: isRecordingType === 'VIDEO',
    audio: true,
    onStop: (blobUrl: string, blob: Blob) => {
      onDrop(
        new File([blob], `Recorded.${isRecordingType === 'VIDEO' ? 'mp4' : 'wav'}`, {
          type: isRecordingType === 'VIDEO' ? 'video/mp4' : 'audio/wav'
        })
      )
    }
  })

  const [anchorElVideo, setAnchorElVideo] = React.useState<null | HTMLElement>(null)
  const [anchorElAudio, setAnchorElAudio] = React.useState<null | HTMLElement>(null)
  const openVideo = Boolean(anchorElVideo)
  const openAudio = Boolean(anchorElAudio)
  const handleClickVideo = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElVideo(event.currentTarget)
  }
  const handleCloseVideo = () => {
    setAnchorElVideo(null)
  }

  const handleClickAudio = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElAudio(event.currentTarget)
  }
  const handleCloseAudio = () => {
    setAnchorElAudio(null)
  }

  const { Component, start, pause, reset } = useCountDown(countdown, hardStop ? stopRecording : undefined)

  const handleExpire = () => {
    // @ts-ignore
    setShowCountdown(false, () => {
      // @ts-ignore
      setHideStartRecording(false)
      startRecording()
      start()
    })
  }

  const handleStart = () => {
    // @ts-ignore
    setHideStartRecording(true, () => {
      // @ts-ignore
      setShowCountdown(true)
    })
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: 422,
        border: '1px solid #7b7b7c',
        borderRadius: '20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexFlow: 'column'
      }}
    >
      <>
        {(status === 'idle' || status === 'recording') &&
          (isRecordingType === 'VIDEO' ? (
            <VideoPreview stream={previewStream} />
          ) : (
            <AudioPreview stream={previewStream} />
          ))}
        {!showCountdown && status === 'recording' ? (
          <>
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                color: redColor.A100
              }}
            >
              <Box sx={{ textAlign: 'center', animation: 'blink 2s ease-in infinite' }}>
                <FiberManualRecordIcon />
              </Box>
              <Component />
            </Box>
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                right: 16,
                zIndex: '1000'
              }}
            >
              <Button
                size="large"
                variant="contained"
                onClick={() => {
                  stopRecording()
                  pause()
                  reset()
                }}
                sx={{
                  fontSize: '0.8rem',
                  color: collectionTheme?.questionnaireContainedButtonTextColor,
                  backgroundColor: collectionTheme?.questionnaireContainedButtonBackgroundColor,
                  ':hover': { backgroundColor: collectionTheme?.questionnaireContainedButtonHoverColor },
                  fontFamily: collectionTheme?.questionnaireTextFontFamily
                }}
              >
                Stop recording
              </Button>
            </Box>
          </>
        ) : (
          mediaBlobUrl && (
            <video
              height={'100%'}
              width="auto"
              src={mediaBlobUrl}
              style={{ maxWidth: '99%', maxHeight: '99%', borderRadius: '20px' }}
              controls
              autoPlay
            />
          )
        )}
      </>
      <Box sx={{ position: 'absolute', width: '100%', height: '100%' }}>
        {showCountdown && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%'
            }}
          >
            <CountDown expire={handleExpire} />
          </Box>
        )}
        {!hideStartRecording && status === 'idle' && (
          <>
            <Box
              sx={{
                position: 'absolute',
                bottom: '10px',
                left: '50%',
                transform: 'translate(-50%)'
              }}
            >
              <ButtonGroup variant="outlined">
                <Button
                  onClick={handleClickVideo}
                  variant="contained"
                  color="secondary"
                  size="large"
                  sx={{ ml: 2 }}
                  aria-controls={openVideo ? 'video-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={openVideo ? 'true' : undefined}
                  endIcon={<SettingsIcon />}
                >
                  Camera
                </Button>
                <Button
                  onClick={handleClickAudio}
                  variant="contained"
                  color="secondary"
                  size="large"
                  sx={{ ml: 2 }}
                  aria-controls={openAudio ? 'audio-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={openAudio ? 'true' : undefined}
                  endIcon={<SettingsIcon />}
                >
                  Microphone
                </Button>
              </ButtonGroup>
              <Menu
                anchorEl={anchorElVideo}
                id="video-menu"
                open={openVideo}
                onClose={handleCloseVideo}
                onClick={handleCloseVideo}
              >
                {isRecordingType === 'VIDEO' && <Divider />}
                {isRecordingType === 'VIDEO' &&
                  devices
                    .filter(item => item.kind === 'videoinput')
                    .map(item => (
                      <MenuItem
                        selected={currentDevices.some(device => device.getSettings().deviceId === item.deviceId)}
                        key={item.deviceId}
                        value={item.deviceId}
                        onClick={() => switchDevice(item.deviceId)}
                      >
                        {item.label}
                      </MenuItem>
                    ))}
              </Menu>
              <Menu
                anchorEl={anchorElAudio}
                id="audio-menu"
                open={openAudio}
                onClose={handleCloseAudio}
                onClick={handleCloseAudio}
              >
                {devices
                  .filter(item => item.kind === 'audioinput')
                  .map(item => (
                    <MenuItem
                      selected={currentDevices.some(device => device.getSettings().deviceId === item.deviceId)}
                      key={item.deviceId}
                      value={item.deviceId}
                      onClick={() => switchDevice(item.deviceId)}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
              </Menu>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                flexFlow: 'column'
              }}
            >
              <ButtonGroup disableElevation size="large" variant="contained">
                <Button
                  size="large"
                  onClick={handleStart}
                  sx={{
                    color: collectionTheme?.questionnaireContainedButtonTextColor,
                    backgroundColor: collectionTheme?.questionnaireContainedButtonBackgroundColor,
                    ':hover': { backgroundColor: collectionTheme?.questionnaireContainedButtonHoverColor },
                    fontFamily: collectionTheme?.questionnaireTextFontFamily
                  }}
                >
                  Start recording
                </Button>
                {!mediaBlobUrl && (
                  <Button
                    size="large"
                    onClick={onChangeRecordingType}
                    sx={{
                      color: collectionTheme?.questionnaireContainedButtonTextColor,
                      backgroundColor: collectionTheme?.questionnaireContainedButtonBackgroundColor,
                      ':hover': { backgroundColor: collectionTheme?.questionnaireContainedButtonHoverColor },
                      fontFamily: collectionTheme?.questionnaireTextFontFamily
                    }}
                  >
                    Change recording type
                  </Button>
                )}
              </ButtonGroup>
            </Box>
          </>
        )}
        {status === 'stopped' && mediaBlobUrl && (
          <Button
            variant="contained"
            size="large"
            onClick={() => {
              clearBlobUrl()
              onDrop(null)
            }}
          >
            Discard
          </Button>
        )}
      </Box>
    </Box>
  )
}

export default Recorder
